import { FC } from 'react';

import type { PlanCourse, RequirementGroup } from '@teachur/http-client';
import { RequirementGroupCard } from '../RequirementGroupCard/RequirementGroupCard';
import { CourseRow } from '../CourseRow/CourseRow';
import { groupAnchorId, sumCredits } from '../../lib/planView';

interface GeneralEducationCardProps {
  groups: RequirementGroup[];
  onSwitch?: (group: RequirementGroup, course: PlanCourse) => void;
}

export const GeneralEducationCard: FC<GeneralEducationCardProps> = ({ groups, onSwitch }) => {
  const allCourses = groups.flatMap((g) => g.courses);
  const required = groups.reduce((t, g) => t + g.requiredCredits, 0);
  const satisfied = sumCredits(allCourses);
  const courseCount = allCourses.length;

  return (
    <RequirementGroupCard
      title="General Education"
      subtitle={`${courseCount} courses · ${required} credits`}
      satisfiedCredits={satisfied}
      requiredCredits={required}
    >
      <div className="flex flex-col gap-5">
        {groups.map((group) => (
          <div key={group.id} id={groupAnchorId(group.id)} className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="[font-size:var(--fs-b4)]! font-semibold text-(--neutral-black-NB900)!">
                {group.title}
              </span>
              <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
                {sumCredits(group.courses)}/{group.requiredCredits} credits
              </span>
            </div>
            {group.courses.length === 0 ? (
              <p className="[font-size:var(--fs-l5)]! m-0! text-(--neutral-black-NB600)!">
                No courses yet.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {group.courses.map((course) => (
                  <CourseRow
                    key={
                      course.source === 'transfer'
                        ? `t-${course.transcriptCourseId}`
                        : `n-${course.courseId}`
                    }
                    course={course}
                    recommended={group.kind !== 'required'}
                    onSwitch={onSwitch ? (c) => onSwitch(group, c) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </RequirementGroupCard>
  );
};
